// Third party
import React from 'react';

// Proposal
import * as ProposalNS from '../../../Proposal';
import { validateDisableRules } from '../../../ProposalHelpers';

// Utils
import { formatLocalizedString } from '@/utils/i18n/i18n';

// Components
import InfoTooltip from '@/views/components/tooltips/InfoTooltip';
import ToggleSwitch from '@/views/components/inputs/ToggleSwitch';

/**
 * Generates the set of hideable layouts for a given section.
 *
 * @param {ProposalNS.ProposalTemplateSectionElement} section - The section containing hideable layouts.
 * @param {boolean} isDisabled - Flag indicating whether the toggle switches should be disabled.
 * @returns {JSX.Element | null} A JSX element containing the hideable layouts, or null if there are no hideable layouts.
 */

type HideableLayoutProps = {
  section: ProposalNS.ProposalTemplateSectionElement;
  isDisabled: boolean;
  selectedSections: string[];
  proposal: ProposalNS.Proposal;
  overrideTooltipPosition?: (coords: { left: number; top: number }) => { left: number; top: number };
  onToggleHiddenLayout: (layout: string, selected: boolean) => void;
  hiddenLayouts: string[];
};

const HideableLayout = ({
  section,
  isDisabled,
  selectedSections,
  proposal,
  overrideTooltipPosition,
  onToggleHiddenLayout,
  hiddenLayouts,
}: HideableLayoutProps) => {
  if (!section.hideableLayouts || selectedSections.indexOf(section.key) === -1) {
    return null;
  }

  const layoutIsHidden = (hiddenLayoutKey: string): boolean => hiddenLayouts.indexOf(hiddenLayoutKey) > -1;

  return (
    <div>
      {section.hideableLayouts.map(config => {
        let layoutDisabled = isDisabled;
        let sidebarTooltip;

        if (!layoutDisabled) {
          const validation =
            config.disableRules == null
              ? { isDisabled: false, sidebarTooltip: undefined }
              : validateDisableRules(config.disableRules, selectedSections, proposal.items, proposal.riskProfile);

          layoutDisabled = validation.isDisabled;
          sidebarTooltip = validation.sidebarTooltip;
        }

        return (
          <div key={config.key} className="proposal-hideable-layout">
            <ToggleSwitch
              onChange={checked => onToggleHiddenLayout(config.key, !checked)}
              checked={!layoutIsHidden(config.key) && !layoutDisabled}
              disabled={layoutDisabled}
              inverted
            >
              <>
                {formatLocalizedString(config.label)}
                {sidebarTooltip && (
                  <InfoTooltip
                    title={formatLocalizedString(sidebarTooltip.title)}
                    message={formatLocalizedString(sidebarTooltip.message)}
                    overridePosition={overrideTooltipPosition}
                  />
                )}
              </>
            </ToggleSwitch>
          </div>
        );
      })}
    </div>
  );
};

export default HideableLayout;
